import React from 'react';
import {
  Box, Typography, TextField, Button, IconButton, Chip, Tooltip,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import NoteAddIcon from '@mui/icons-material/NoteAdd';
import BuildIcon from '@mui/icons-material/Build';
import firebase from '../../utilities/firebase';
import { StateManager } from '../../utilities/stateManager';
import moment from 'moment';

const TS_FORMAT = 'YYYY/MM/DD HH:mm';

// A clock-out note is the mechanic's own record of the work, cleaned by Claude and
// posted here by the cleanServiceNote function. Staff can read it but not rewrite
// or remove it — see the guard in saveEdit/deleteNote.
const isClockOut = (note) => note && note.source === 'clockOut';

const sameNote = (a, b) => (
  !!a && !!b
  && a.value === b.value
  && a.timestamp === b.timestamp
  && (a.timeLogId || null) === (b.timeLogId || null)
);

// The order doc is also appended to by the backend (cleanServiceNote and
// emailServiceDocument both arrayUnion). Indexes of existing entries survive an
// append, but re-find the note anyway so a mutation can never hit the wrong row.
const locateNote = (arr, target, hintIndex) => {
  if (sameNote(arr[hintIndex], target)) return hintIndex;
  return arr.findIndex(n => sameNote(n, target));
};

export default function Notes({ order }) {
  const [newNoteText, setNewNoteText] = React.useState('');
  const [notes, setNotes] = React.useState(order.notes || []);
  const [editingKey, setEditingKey] = React.useState(null);
  const [editingText, setEditingText] = React.useState('');

  React.useEffect(() => {
    setNotes(order.notes || []);
  }, [order.notes]);

  // Mutate the notes array inside a transaction rather than overwriting it. The
  // old whole-array .update() raced the backend appenders and silently dropped
  // whichever note lost.
  const mutateNotes = async (apply) => {
    const ref = firebase.firestore().doc('orders/' + order.stock);
    try {
      await firebase.firestore().runTransaction(async (tx) => {
        const doc = await tx.get(ref);
        const current = (doc.exists && doc.data().notes) || [];
        const next = apply(current);
        if (!next) return;
        tx.update(ref, { notes: next });
      });
    } catch (e) {
      console.error('Notes write failed:', e);
      StateManager.setAlertAndOpen('Could not save the note: ' + (e?.message || e), 'error');
      setNotes(order.notes || []);
    }
  };

  const addNote = async () => {
    const text = newNoteText.trim();
    if (!text) return;
    const note = { value: text, timestamp: moment().format(TS_FORMAT) };
    const author = StateManager.userName;
    if (author) note.author = author;
    setNewNoteText('');
    setNotes(prev => [...prev, note]);
    await mutateNotes(current => [...current, note]);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) addNote();
  };

  const startEdit = (key, note) => {
    setEditingKey(key);
    setEditingText(note.value);
  };

  const cancelEdit = () => {
    setEditingKey(null);
    setEditingText('');
  };

  const saveEdit = async (note, hintIndex) => {
    const text = editingText.trim();
    if (!text || isClockOut(note)) return;
    setEditingKey(null);
    setEditingText('');
    await mutateNotes((current) => {
      const i = locateNote(current, note, hintIndex);
      if (i === -1) return null;
      const next = current.slice();
      next[i] = { ...current[i], value: text };
      return next;
    });
  };

  const deleteNote = async (note, hintIndex) => {
    if (isClockOut(note)) return;
    if (!window.confirm('Delete this note?')) return;
    await mutateNotes((current) => {
      const i = locateNote(current, note, hintIndex);
      if (i === -1) return null;
      return current.filter((_, idx) => idx !== i);
    });
  };

  // Newest first. Sorting on the timestamp rather than array position keeps
  // backfilled clock-out notes in their real place in the history.
  const ordered = React.useMemo(() => (
    notes
      .map((note, index) => ({ note, index }))
      .sort((a, b) => {
        const cmp = String(b.note.timestamp || '').localeCompare(String(a.note.timestamp || ''));
        return cmp !== 0 ? cmp : b.index - a.index;
      })
  ), [notes]);

  return (
    <Box sx={{ py: 1 }}>
      {/* Add note area */}
      <Box sx={{ mb: 3 }}>
        <TextField
          value={newNoteText}
          onChange={e => setNewNoteText(e.target.value)}
          onKeyDown={handleKeyDown}
          label="New Note"
          placeholder="Add a note... (Ctrl+Enter to save)"
          multiline
          minRows={3}
          fullWidth
          size="small"
        />
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
          <Button
            variant="contained"
            size="small"
            startIcon={<NoteAddIcon />}
            onClick={addNote}
            disabled={!newNoteText.trim()}
          >
            Add Note
          </Button>
        </Box>
      </Box>

      {ordered.length > 0 && (
        <>
          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1.5, textTransform: 'uppercase', letterSpacing: 0.5, fontSize: 11 }}>
            {ordered.length} {ordered.length === 1 ? 'Note' : 'Notes'}
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {ordered.map(({ note, index }) => {
              const key = `${index}-${note.timestamp}`;
              const mechanic = isClockOut(note);
              const editing = editingKey === key;
              return (
                <Box
                  key={key}
                  sx={{
                    borderRadius: 1,
                    border: '1px solid',
                    borderColor: mechanic ? 'info.light' : 'divider',
                    bgcolor: mechanic ? 'action.hover' : 'grey.50',
                    px: 2,
                    py: 1.5,
                  }}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                      <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.8 }}>
                        {moment(note.timestamp, TS_FORMAT).format('MMM D, YYYY h:mm a')}
                      </Typography>
                      {mechanic ? (
                        <Chip
                          size="small"
                          icon={<BuildIcon />}
                          label={note.author ? `${note.author} · clock-out` : 'Clock-out'}
                          sx={{ height: 20, fontSize: 11 }}
                        />
                      ) : note.author ? (
                        <Typography variant="caption" color="text.secondary">
                          {note.author}
                        </Typography>
                      ) : null}
                    </Box>
                    <Box sx={{ display: 'flex', gap: 0.5, ml: 1, flexShrink: 0 }}>
                      {mechanic ? (
                        <Tooltip title="Clock-out notes are the mechanic's record and cannot be changed">
                          <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.8 }}>
                            Read only
                          </Typography>
                        </Tooltip>
                      ) : editing ? (
                        <>
                          <IconButton size="small" color="primary" onClick={() => saveEdit(note, index)}>
                            <CheckIcon fontSize="small" />
                          </IconButton>
                          <IconButton size="small" onClick={cancelEdit}>
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        </>
                      ) : (
                        <>
                          <IconButton size="small" onClick={() => startEdit(key, note)} sx={{ color: 'text.secondary' }}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton size="small" color="error" onClick={() => deleteNote(note, index)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </>
                      )}
                    </Box>
                  </Box>

                  {editing ? (
                    <TextField
                      value={editingText}
                      onChange={e => setEditingText(e.target.value)}
                      multiline
                      fullWidth
                      autoFocus
                      size="small"
                      sx={{ mt: 0.5 }}
                    />
                  ) : (
                    <Typography variant="body2" sx={{ mt: 0.5, whiteSpace: 'pre-wrap' }}>
                      {note.value}
                    </Typography>
                  )}
                </Box>
              );
            })}
          </Box>
        </>
      )}

      {ordered.length === 0 && (
        <Box sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>
          <Typography variant="body2">No notes yet</Typography>
        </Box>
      )}
    </Box>
  );
}
